import { useRef } from "react";
import { Modal, Typography } from "antd";
import DiscordLink from "../common/DiscordLink.js";

const { Paragraph, Title } = Typography;

export const LEGAL_DOCUMENT = {
  TERMS: "terms",
  PRIVACY: "privacy",
};

function TermsOfService() {
  return (
    <Typography>
      <Paragraph type="secondary">Last updated: 12 August 2026</Paragraph>
      <Title level={4}>1. Your account</Title>
      <Paragraph>You must be at least 13 years old to create an account. If local law requires parental consent for you to use the service, you must have that consent.</Paragraph>
      <Paragraph>You must provide accurate registration information, keep your login credentials secure, and use only accounts you are authorized to use. You are responsible for activity performed through your account.</Paragraph>
      <Paragraph>You may not sell, transfer, share, or allow another person to use your account.</Paragraph>

      <Title level={4}>2. Fair play and acceptable use</Title>
      <Paragraph>You may not cheat, automate gameplay without permission, exploit bugs, harass or threaten other players, impersonate others, disrupt the service, or use Age of Companies for unlawful activity.</Paragraph>
      <Paragraph>You may not use in-game communication to post or send unlawful, abusive, threatening, or seriously harmful content.</Paragraph>
      <Paragraph>You may not buy, sell, or exchange in-game assets for real-world money or other things of real-world value.</Paragraph>

      <Title level={4}>3. Game service</Title>
      <Paragraph>Age of Companies is an evolving game. Features, rules, balance, virtual items, virtual currency, game progress, and availability may change as the game develops.</Paragraph>
      <Paragraph>Virtual currency and game items have no real-world monetary value unless explicitly stated otherwise.</Paragraph>

      <Title level={4}>4. Content and enforcement</Title>
      <Paragraph>You retain responsibility for content you submit through the service. You grant us permission to store, display, and process that content as reasonably necessary to operate, moderate, and provide the game.</Paragraph>
      <Paragraph>We may remove content that is abusive, unlawful, or violates these terms.</Paragraph>

      <Title level={4}>5. Suspension and termination</Title>
      <Paragraph>We may restrict, suspend, or terminate accounts that violate these terms or where reasonably necessary to protect the game, its users, or the service.</Paragraph>
      <Paragraph>Suspension, termination, or closure of an account may result in loss of game progress, virtual currency, and virtual items.</Paragraph>

      <Title level={4}>6. Availability and liability</Title>
      <Paragraph>We do not guarantee that the game will always be available, uninterrupted, error-free, or that game progress will never be lost. To the extent permitted by law, we are not liable for indirect loss, lost game progress, or interruptions to the service.</Paragraph>
      <Paragraph>Nothing in these terms excludes or limits any liability or mandatory consumer rights that cannot legally be excluded or limited.</Paragraph>

      <Title level={4}>7. Changes</Title>
      <Paragraph>You will be notified through the service if material changes are made to these Terms of Service.</Paragraph>

      <Title level={4}>8. Governing law and contact</Title>
      <Paragraph>These terms are governed by Norwegian law, without limiting any mandatory consumer rights that apply to you.</Paragraph>
      <Paragraph>
        Questions about these terms and general support requests can be submitted in the official Age of Companies Discord server. <DiscordLink />
      </Paragraph>
    </Typography>
  );
}

function PrivacyPolicy() {
  return (
    <Typography>
      <Paragraph type="secondary">Last updated: 12 August 2026</Paragraph>
      <Title level={4}>1. Controller and contact</Title>
      <Paragraph>Age of Companies is operated by Erik Kvalø in Norway, who is the controller responsible for the personal data described in this Privacy Policy.</Paragraph>
      <Paragraph>
        Privacy questions and requests concerning your personal data can be sent to privacy@ageofcompanies.com. General support is also available through the official Age of Companies Discord server. <DiscordLink />
      </Paragraph>

      <Title level={4}>2. Data we process</Title>
      <Paragraph>We process information associated with your account and use of Age of Companies, including:</Paragraph>
      <ul>
        <li>username;</li>
        <li>email address;</li>
        <li>hashed password;</li>
        <li>account status and account creation information;</li>
        <li>login and authentication information;</li>
        <li>email-verification and password-reset information;</li>
        <li>gameplay records and game progress;</li>
        <li>communications and content you submit through the service, including public chat messages and private messages between users; and</li>
        <li>technical and security information generated when you use the service, such as IP addresses, browser requests, error information, and security logs.</li>
      </ul>
      <Paragraph>We do not store your password in plain text.</Paragraph>

      <Title level={4}>3. Purposes and legal bases</Title>
      <Paragraph>We process account and gameplay information to create and maintain your account, provide the game, preserve game progress, authenticate users, and send essential account messages. This processing is necessary to perform our agreement with you.</Paragraph>
      <Paragraph>We process in-game communications to provide communication features and, where necessary, to enforce our Terms of Service, investigate reports of abuse, maintain security, and protect users and the service.</Paragraph>
      <Paragraph>Private messages are intended to be visible to their participants but may be accessed or reviewed by the Age of Companies operator where reasonably necessary to investigate reports of abuse, enforce the Terms of Service, maintain security, or comply with legal obligations.</Paragraph>
      <Paragraph>We process security, moderation, diagnostic, and service-improvement information where necessary for our legitimate interests in operating, protecting, maintaining, and improving a safe and reliable service.</Paragraph>

      <Title level={4}>4. Third parties</Title>
      <Paragraph>Age of Companies is hosted by Hostinger. We also use Hostinger's email service to send account-related emails, such as email verification and password-reset emails. This means Hostinger processes information needed to deliver those emails, such as your email address and the contents of the email.</Paragraph>
      <Paragraph>We may also disclose personal data where required by law or where reasonably necessary to establish, exercise, or defend legal claims.</Paragraph>
      <Paragraph>We do not sell your personal data.</Paragraph>

      <Title level={4}>5. Retention</Title>
      <Paragraph>Account and gameplay information is generally retained while your account is active.</Paragraph>
      <Paragraph>After an account is closed, we delete or anonymize personal data when it is no longer reasonably necessary to operate the service, resolve disputes, enforce our terms, maintain security, or comply with legal obligations.</Paragraph>

      <Title level={4}>6. Your rights</Title>
      <Paragraph>Subject to applicable law, you may have the right to:</Paragraph>
      <ul>
        <li>request access to and a copy of your personal data;</li>
        <li>request correction of inaccurate personal data;</li>
        <li>request deletion of your personal data;</li>
        <li>request restriction of processing;</li>
        <li>receive certain personal data in a portable format;</li>
        <li>object to processing based on legitimate interests;</li>
      </ul>
      <Paragraph>You also have the right to lodge a complaint with the relevant data-protection supervisory authority. In Norway, this is Datatilsynet.</Paragraph>

      <Title level={4}>7. Browser storage</Title>
      <Paragraph>Age of Companies uses browser storage where necessary for authentication, session management, security, and game preferences.</Paragraph>
      <Paragraph>This storage is used to keep you signed in, protect your account, remember relevant preferences, and provide requested game functionality.</Paragraph>

      <Title level={4}>8. Changes</Title>
      <Paragraph>You will be notified through the service if material changes are made to this Privacy Policy.</Paragraph>
    </Typography>
  );
}

export function LegalDocumentModal({ document, open, onClose }) {
  const showTerms = document === LEGAL_DOCUMENT.TERMS;
  const documentContainerRef = useRef(null);

  const resetDocumentScroll = (isOpen) => {
    if (isOpen && documentContainerRef.current) {
      documentContainerRef.current.scrollTop = 0;
    }
  };

  return (
    <Modal key={document} title={showTerms ? "Terms of Service" : "Privacy Policy"} open={open} onCancel={onClose} afterOpenChange={resetDocumentScroll} footer={null} width={760} destroyOnHidden>
      <div ref={documentContainerRef} style={{ maxHeight: "65vh", overflowY: "auto" }}>
        {showTerms ? <TermsOfService /> : <PrivacyPolicy />}
      </div>
    </Modal>
  );
}

export function LegalDocumentLinks({ onOpen }) {
  const openDocument = (event, document) => {
    event.preventDefault();
    event.stopPropagation();
    onOpen(document);
  };

  return (
    <>
      <a href="#terms-of-service" onClick={(event) => openDocument(event, LEGAL_DOCUMENT.TERMS)}>
        Terms of Service
      </a>{" "}
      and{" "}
      <a href="#privacy-policy" onClick={(event) => openDocument(event, LEGAL_DOCUMENT.PRIVACY)}>
        Privacy Policy
      </a>
    </>
  );
}
